import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Clear, Close, InfoOutlined } from "@material-ui/icons";
import useNotifier from "@saleor/hooks/useNotifier";
import { DialogProps } from "@saleor/types";
import { DeliveryTypeEnum } from "@saleor/types/globalTypes";
import React from "react";

const useStyles = makeStyles(
  theme => ({
    chip: {
      "&:focus": {
        backgroundColor: "#ACCAFF"
      },
      backgroundColor: "#ACCAFF",
      borderRadius: "5px",
      color: "#002972",
      margin: "2px 5px"
    },
    chipDeleteIcon: {
      "&:hover": {
        color: "#002972"
      },
      color: "#002972",
      width: "17px"
    },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      marginTop: "3px"
    },
    closeButton: {
      color: theme.palette.grey[500],
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1)
    },
    contentActionButton: {
      "& span": {
        color: "white",
        fontWeight: 700
      },
      "&:hover": {
        "& span": {
          color: "white",
          fontWeight: 700
        },
        backgroundColor: "#0030FF",
        color: "white"
      },
      backgroundColor: "#0030FF",
      color: "white"
    },
    contentActionButtonDisabled: {
      "& span": {
        color: "white",
        fontWeight: 700
      },
      backgroundColor: "#1848FE !important",
      color: "white",
      opacity: "30%"
    },
    contentActionSpiel: {
      alignItems: "center",
      display: "flex",
      justifyContent: "left",
      width: "100%"
    },
    contentActionSpielText: {
      color: "#747C8C"
    },
    contentText: {
      fontSize: "17px",
      fontWeight: 400,
      minHeight: "90px"
    },
    dialogTitle: {
      fontWeight: 700,
      padding: "5px 5px 0px 5px"
    },
    formControl: {
      border: "solid #BFBDBD 1px",
      borderRadius: "5px",
      margin: theme.spacing(1),
      paddingLeft: "5px",
      width: "98%"
    },
    formControlLabel: {
      height: "20px"
    }
  }),
  {
    name: "SelectDeliveryTypeDialog"
  }
);

interface SelectDeliveryTypeDialogProps extends DialogProps {
  onConfirm: (delTypes: string[]) => void;
  defaultDelTypes: string[];
  optionsDelTypes: Array<{
    label: string;
    value: DeliveryTypeEnum;
  }>;
}

const SelectDeliveryTypeDialog: React.FC<SelectDeliveryTypeDialogProps> = props => {
  const { open, onClose, onConfirm, defaultDelTypes, optionsDelTypes } = props;
  const classes = useStyles(props);
  const notify = useNotifier();

  const [selectedDelTypes, setSelectedDelTypes] = React.useState(
    defaultDelTypes
  );

  const handleChange = event => {
    setSelectedDelTypes(event.target.value);
  };

  const handleClose = () => {
    if (selectedDelTypes.length > 0 && defaultDelTypes.length > 0) {
      onClose();
    } else {
      notify({
        status: "error",
        text: "No Delivery Type(s) selected."
      });
    }
  };

  const checkDisable = (name: string) =>
    !(selectedDelTypes.includes(name) || selectedDelTypes.length < 3);

  const handleDisplayValue = value =>
    optionsDelTypes.find(item => item.value === value)?.label;

  return (
    <Dialog onClose={() => handleClose()} open={open} maxWidth="md" fullWidth>
      <>
        <DialogTitle>
          <Typography className={classes.dialogTitle} variant="h5">
            Delivery Type Selection
          </Typography>

          {selectedDelTypes.length > 0 && defaultDelTypes.length > 0 && (
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => handleClose()}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <div className={classes.contentText}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel
                id="inputSelectedDelTypes"
                className={classes.formControlLabel}
              >
                {selectedDelTypes.length === 0
                  ? "Select a Delivery Type"
                  : "Delivery Type(s)"}
              </InputLabel>
              <Select
                labelId="inputSelectedDelTypes"
                id="checkboxSelectedDelTypes"
                multiple
                value={selectedDelTypes}
                onChange={handleChange}
                input={<Input />}
                variant="outlined"
                renderValue={(selected: any) => (
                  <div className={classes.chips}>
                    {selected.map(value => (
                      <Chip
                        key={value}
                        label={handleDisplayValue(value)}
                        className={classes.chip}
                        deleteIcon={
                          <Clear className={classes.chipDeleteIcon} />
                        }
                        onDelete={() =>
                          setSelectedDelTypes(oldValue =>
                            oldValue.filter(item => item !== value)
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              >
                {optionsDelTypes.map(delType => (
                  <MenuItem
                    key={delType.value}
                    value={delType.value}
                    disabled={checkDisable(delType.value)}
                  >
                    <Checkbox
                      checked={selectedDelTypes.indexOf(delType.value) > -1}
                      disabled={checkDisable(delType.value)}
                    />
                    <ListItemText primary={delType.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <div className={classes.contentActionSpiel}>
            <InfoOutlined
              fontSize="small"
              className={classes.contentActionSpielText}
            />
            <p className={classes.contentActionSpielText}>
              The displayed mother orders will be based on the delivery type(s)
              selected.
            </p>
          </div>
          <div>
            <Button
              id="dialogActionConfirmBtn"
              autoFocus
              onClick={() => onConfirm(selectedDelTypes)}
              color="primary"
              variant="contained"
              className={
                !selectedDelTypes || selectedDelTypes.length === 0
                  ? classes.contentActionButtonDisabled
                  : classes.contentActionButton
              }
              disabled={!selectedDelTypes || selectedDelTypes.length === 0}
            >
              Proceed
            </Button>
          </div>
        </DialogActions>
      </>
    </Dialog>
  );
};

export default SelectDeliveryTypeDialog;
